/**
 * Standalone Sequences List Component
 *
 * Professional list view for standalone sequences with:
 * - Infinite scroll
 * - Clean list design matching Broadcasts
 * - Search and filter capabilities
 * - Status badges and actions
 * - Responsive layout
 * - Create sequence modal
 *
 * @component
 */

import React, { useEffect, useState, useCallback, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Modal,
  Skeleton,
  Tooltip,
  Popover,
} from 'antd';
import {
  PlusOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import InfiniteScroll from 'react-infinite-scroll-component';

// assets
import Delete from '../../../assets/customSVG/Delete';
import Edit from '../../../assets/customSVG/Edit';
import Play from '../../../assets/customSVG/Play';
import ClearFilters from '../../../assets/customSVG/ClearFilters';
import BranchIcon from '../../../assets/customSVG/BranchIcon';
import PremiumUpgradeScreen from '../../../components/commonComponents/PremiumUpgradeScreen/PremiumUpgradeScreen';
import { images } from '../../../assets/images/index';
import { ICONS } from '../../../assets/icons';

// hooks
import { useFeature } from '../../../hooks/useFeatureGating';

// constants
import { FEATURES } from '../../../constant/featureSlugs';

// helpers
import { cn } from '../../../helper/cn';
import { dateConverter } from '../../../helper/dateConverter';
import { useAspSelector } from 'test/jest-redux-hooks';

// api
import {
  fetchSequencesList,
  deleteSequence,
  createStandaloneSequence,
  changeSequenceStatus,
} from '../api/sequencesApi';

// constants
import { STATUS, SORT, SEQUENCE_STYLES } from '../constants/sequenceList';

// components
import Loader from '../../../components/commonComponents/Loader/Index';
import NoData from '../../../components/commonComponents/Empty/NoData';
import Header from '../../../components/commonComponents/modals/Header';
import SearchBar from '../../../components/commonComponents/searchBar';
import CheckBoxSqure from '../../../components/commonComponents/checkbox/CheckBoxSqure';
import NewRadioButton from '../../../components/form/radio/NewRadio';
import MonthCalender from '../../../components/commonComponents/calendar/MonthCalender';

import NotificationContext from '../../../providers/NotificationProvider';
import {
  showSuccessToast,
  showErrorToast,
} from '../../../components/commonComponents/gxAntdToast/toastUtils';

const MAX_SEQUENCE_NAME_LENGTH = 50;

const SequencesList = () => {
  const navigate = useNavigate();
  const { currentBrand } = useAspSelector((state) => state.app);
  const listContainerRef = useRef(null);
  const notification = useContext(NotificationContext);

  // Feature Gating
  const { isEnabled: canSequence, isLoading: sequenceLoading } = useFeature(FEATURES.SEQUENCE_CAMPAIGN);

  // State management
  const [sequences, setSequences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchKey, setSearchKey] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Filter and Sort states
  const [status, setStatus] = useState(STATUS);
  const [sort, setSort] = useState(SORT);
  const [sortLabel, setSortLabel] = useState('Newest First');

  // Create modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [sequenceData, setSequenceData] = useState({
    name: '',
    start_date: null,
    end_date: null,
  });

  /**
   * Fetch sequences from API
   */
  const fetchSequences = useCallback(
    async (isLoadMore = false) => {
      if (!currentBrand?.brand_id) return;

      try {
        if (!isLoadMore) {
          setLoading(true);
          setSequences([]);
          setPage(1);
          setHasMore(true);
        }

        const currentPage = isLoadMore ? page + 1 : 1;

        // Extract active filters
        const statusFilter = status
          .filter((s) => s.isSelected)
          .map((s) => s.status)
          .join(',');

        // Extract active sort
        const selectedOption = sort
          .flatMap((s) => s.options)
          .find((o) => o.isSelected);

        const sortBy = selectedOption?.field || 'created_at';
        const sortOrder = selectedOption?.status || 'desc';

        const response = await fetchSequencesList(currentBrand.brand_id, {
          page: currentPage,
          limit: 20,
          search: debouncedSearch,
          status: statusFilter,
          sortBy: sortBy,
          sortOrder: sortOrder,
        });

        if (response?.status === 200) {
          const data = response.data?.sequences || [];
          const totalCount = response.data?.total || 0;

          if (isLoadMore) {
            setSequences((prev) => [...prev, ...data]);
            setPage(currentPage);
          } else {
            setSequences(data);
            setPage(1);
          }

          setTotal(totalCount);
          if (
            data.length < 20 ||
            (isLoadMore && sequences.length + data.length >= totalCount)
          ) {
            setHasMore(false);
          } else {
            setHasMore(
              data.length > 0 &&
                (isLoadMore
                  ? sequences.length + data.length < totalCount
                  : data.length < totalCount),
            );
          }
        } else {
          showErrorToast(
            notification,
            response?.response?.data?.error || 'Failed to load sequences',
          );
          setHasMore(false);
        }
      } catch (error) {
        showErrorToast(notification, 'Failed to load sequences');
        setHasMore(false);
        console.error('Error fetching sequences:', error);
      } finally {
        setLoading(false);
      }
    },
    [
      currentBrand?.brand_id,
      page,
      debouncedSearch,
      sequences.length,
      notification,
      status,
      sort,
    ],
  );

  // Debounce search input by 500ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchKey);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchKey]);

  useEffect(() => {
    fetchSequences(false);
  }, [debouncedSearch, status, sort, currentBrand?.brand_id]);

  const loadNextPage = () => {
    fetchSequences(true);
  };

  const handleSearch = (value) => {
    setSearchKey(value);
  };

  const onStatusChange = (item) => {
    setStatus((prev) =>
      prev.map((s) =>
        s.id === item.id ? { ...s, isSelected: !s.isSelected } : s,
      ),
    );
  };

  const clearStatus = () => {
    setStatus((prev) => prev.map((s) => ({ ...s, isSelected: false })));
  };

  const onSortChange = (sectionId, item) => {
    setSort((prev) =>
      prev.map((section) => ({
        ...section,
        options: section.options.map((option) => ({
          ...option,
          isSelected: option.id === item.id,
        })),
      })),
    );
    setSortLabel(item.label);
  };

  const allowAllClear = () => {
    return status.some((s) => s.isSelected);
  };

  /**
   * Handle sequence deletion
   */
  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Delete Sequence',
      content: `Are you sure you want to delete "${record.name}"? This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => {
        deleteSequence(record.id)
          .then((res) => {
            if (res?.status === 200) {
              showSuccessToast(notification, 'Sequence deleted successfully');
              fetchSequences(false);
            } else {
              showErrorToast(notification, 'Failed to delete sequence');
            }
          })
          .catch((error) => {
            showErrorToast(notification, 'Failed to delete sequence');
            console.error('Error deleting sequence:', error);
          });
      },
    });
  };

  /**
   * Handle sequence edit - navigate to flow editor
   */
  const handleEdit = (record) => {
    navigate(`/user/sequences/${record.id}/flow`, { state: { campaign_id: record.campaign_id } });
  };

  /**
   * Handle pause/resume sequence
   */
  const handlePauseResume = async (record) => {
    const action = ['ACTIVE', 'RUNNING', 'SCHEDULED'].includes(record.status)
      ? 'pause'
      : 'start';
    try {
      const res = await changeSequenceStatus(currentBrand.brand_id, record.id, record.campaign_id, action);
      if (res?.status === 200) {
        showSuccessToast(
          notification,
          action === 'pause' ? 'Sequence paused' : 'Sequence resumed',
        );
        fetchSequences(false);
      } else {
        showErrorToast(notification, res?.response?.data?.error || 'Failed to update sequence');
      }
    } catch (error) {
      showErrorToast(notification, 'Failed to update sequence');
      console.error('Error updating sequence status:', error);
    }
  };

  /**
   * Handle row click to view details
   */
  const handleNavigateSequence = (record) => {
    navigate(`/user/sequences/${record.id}`, { state: { campaign_id: record.campaign_id } });
  };

  /**
   * Open create modal
   */
  const openCreateModal = () => {
    setSequenceData({
      name: '',
      start_date: null,
      end_date: null,
    });
    setIsCreateModalOpen(true);
  };

  /**
   * Close create modal
   */
  const closeCreateModal = () => {
    setCalendarOpen(false);
    setIsCreateModalOpen(false);
    setSequenceData({
      name: '',
      start_date: null,
      end_date: null,
    });
  };

  /**
   * Validate if sequence can be created
   */
  const canCreateSequence = useCallback(() => {
    const today = dayjs();
    const startDate = dayjs(sequenceData.start_date);
    const endDate = dayjs(sequenceData.end_date);

    const hasName = sequenceData.name?.length > 0 && sequenceData.name?.length <= MAX_SEQUENCE_NAME_LENGTH;
    const hasStartDate = sequenceData.start_date !== null;
    const hasEndDate = sequenceData.end_date !== null;
    const startDateValid = startDate.format('YYYY-MM-DD') >= today.format('YYYY-MM-DD');
    const endDateAfterStart = endDate.format('YYYY-MM-DD') > startDate.format('YYYY-MM-DD');

    return hasName && hasStartDate && hasEndDate && startDateValid && endDateAfterStart;
  }, [sequenceData]);

  /**
   * Create new standalone sequence
   */
  const handleCreateSequence = async () => {
    if (isCreating || !canCreateSequence()) return;

    setIsCreating(true);
    try {
      const payload = {
        brand_id: currentBrand?.brand_id,
        name: sequenceData.name,
        start_date: dayjs(sequenceData.start_date).format('YYYY-MM-DD HH:mm:ss'),
        end_date: dayjs(sequenceData.end_date).format('YYYY-MM-DD HH:mm:ss'),
      };

      const response = await createStandaloneSequence(payload);

      if (response?.status === 200) {
        showSuccessToast(notification, 'Sequence created successfully');
        closeCreateModal();
        const sequenceId = response.data?.data?.sequence?.id;
        const campaignId = response.data?.campaign_id;
        if (sequenceId) {
          navigate(`/user/sequences/${sequenceId}/flow`, { state: { campaign_id: campaignId } });
        } else {
          fetchSequences(false);
        }
      } else {
        showErrorToast(notification, response?.response?.data?.error || 'Failed to create sequence');
      }
    } catch (error) {
      console.error('Error creating sequence:', error);
      showErrorToast(notification, error?.response?.data?.error || 'Failed to create sequence');
    } finally {
      setIsCreating(false);
    }
  };

  const bodyStyle = 'text-sm weight-medium text-[var(--font-600)] text-left pl-2';

  return (
    <>
      {canSequence ? (
        <div className="w-full p-20 flex flex-col gap-10 pb-0 relative min-h-screen bg-[#F9FAFB] bg-[radial-gradient(circle_at_18%_10%,rgba(10,227,176,0.14),transparent_32%),radial-gradient(circle_at_82%_12%,rgba(124,58,237,0.12),transparent_30%),linear-gradient(180deg,#f9f9fc_0%,#f7f8fb_60%,#f5f7fb_100%)]">
      {/* Header Section */}
      <div className="w-100 flex items-center justify-between my-7">
        <div className="w-[65%] flex-row align-center gap-5">
          <img src={images?.Sequence} alt="sequence" />
          <div className="flex-column gap-2.5">
            <h2 className="text-3xl weight-semibold">Sequences</h2>
            <p className="header-titles">
              Automate and build intelligent sequences
              that respond to user behavior and deliver personalized messages at
              the right time.
            </p>
          </div>
        </div>
        <div
          className="flex-row align-center px-4 py-2 text-base weight-medium rounded-md !bg-[var(--new-primary)] border-2 border-[var(--new-primary)] text-white cursor-pointer transition-colors shadow-sm"
          onClick={openCreateModal}
        >
          <BranchIcon color="#FFFFFF" className="mr-3" />
          <span>Create Sequence</span>
        </div>
      </div>

      <div className="w-full h-full bg-white rounded-t-2xl px-5 flex flex-col flex-1 shadow-sm border border-[#E8E8EA]">
        {/* Filter/Search Section */}
        <div className="w-full py-5 flex items-center justify-between gap-5 border-b border-[var(--border-50)]">
          <h4 className="new-header3">
            {total > 0 ? `${total} Total Sequences` : 'Sequences'}
          </h4>
          <div className="flex items-center gap-5">
            <div>
              <SearchBar
                searchKey={searchKey}
                setSearchKey={setSearchKey}
                onChange={handleSearch}
                loading={false}
                expandable={false}
              />
            </div>
            <Popover
              trigger={['click']}
              placement="bottom"
              arrow={false}
              content={
                <div className="min-w-56 px-3.5 py-2.5">
                  {sort?.map((section) => (
                    <div key={section?.heading}>
                      <h3 className="new-header1 mb-2 pt-5">
                        {section.heading}
                      </h3>
                      {section?.options?.map((s) => (
                        <div
                          key={s.id}
                          className={cn(
                            'w-full h-9 flex items-center gap-2.5 py-1 cursor-pointer opacity-100',
                          )}
                          onClick={() => onSortChange(section?.id, s)}
                        >
                          <div className="w-5 h-9 flex items-center justify-center mt-1">
                            <NewRadioButton
                              value={s?.isSelected}
                              selected={s?.isSelected}
                              onChange={() => {}}
                              rootClassName={s?.isSelected && 'size-3.5 new-radio'}
                            />
                          </div>
                          {s?.icon && <img src={s?.icon} alt={s.label} />}
                          <p className="font-medium text-sm text-[#202421]">
                            {s?.label}
                          </p>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              }
            >
              <div className="group/sort flex items-center gap-2.5 bg-[#F9FAFB] border border-[#E8E8EA] px-3 py-1.5 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <div className="text-sm font-medium text-[#616874]">
                  {sortLabel || 'Newest First'}
                </div>
                <div className="border-[4px] border-transparent border-t-[#616874] translate-y-[2px]"></div>
              </div>
            </Popover>

            <Popover
              trigger={['click']}
              arrow={false}
              placement="bottom"
              content={
                <div className="min-w-56 px-3.5 py-2.5">
                  <div className="w-full h-8 flex items-center justify-between gap-5 py-2.5 text-sm weight-medium">
                    <div className="text-[var(--font-400)] text-[#616874]">
                      Status
                    </div>
                    <div
                      className={cn(
                        'text-[#F96056]',
                        status.some((s) => s.isSelected)
                          ? 'cursor-pointer opacity-100'
                          : 'cursor-not-allowed opacity-50',
                      )}
                      onClick={() => {
                        if (status.some((s) => s.isSelected)) clearStatus();
                      }}
                    >
                      Clear
                    </div>
                  </div>
                  {status?.map((s) => (
                    <div
                      className={cn(
                        'w-full h-9 flex items-center gap-2.5 py-1 cursor-pointer',
                      )}
                      onClick={() => onStatusChange(s)}
                      key={s?.id}
                    >
                      <CheckBoxSqure
                        value={s?.isSelected}
                        onChange={() => {}}
                        className="size-4"
                      />
                      {s?.icon && (
                        <img src={s?.icon} alt={s.label} className="w-4" />
                      )}
                      <p className="font-medium text-sm text-[#202421]">
                        {s?.label}
                      </p>
                    </div>
                  ))}
                </div>
              }
            >
              <div className="group/goal flex items-center gap-2.5 bg-[#F9FAFB] border border-[#E8E8EA] px-3 py-1.5 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <div className="text-sm font-medium text-[#616874] flex items-center">
                  Status
                  {status.some((s) => s.isSelected) && (
                    <div className="ml-1.5 rounded-full bg-[#6940F2] text-white text-[10px] w-4 h-4 flex justify-center items-center font-bold">
                      {status.filter((s) => s.isSelected).length}
                    </div>
                  )}
                </div>
                <div className="border-[4px] border-transparent border-t-[#616874] translate-y-[2px]"></div>
              </div>
            </Popover>

            <div
              className={cn(
                'group/status flex items-center gap-2.5 hover:bg-gray-50 px-2.5 py-2.5 rounded-lg transition-colors',
                allowAllClear()
                  ? 'cursor-pointer opacity-100'
                  : 'cursor-not-allowed opacity-50',
              )}
              onClick={() => {
                if (allowAllClear()) clearStatus();
              }}
            >
              <ClearFilters
                width={16}
                height={16}
                color={allowAllClear() ? '#202421' : '#616874'}
              />
            </div>
          </div>
        </div>

        <div className="w-full relative h-full overflow-hidden flex-1">
          <Skeleton
            loading={loading && sequences.length === 0}
            active
            paragraph={{ rows: 8 }}
          >
            {sequences?.length > 0 && (
              <div className="w-full h-14 sticky top-0 flex items-center pr-1 border-b border-gray-100 bg-white z-10">
                <div className={cn('new-header2', 'w-[35%] pl-3')}>
                  Sequence Name
                </div>
                <div className={cn('new-header2', 'w-[15%] text-center')}>
                  Status
                </div>
                <div className={cn('new-header2', 'w-[15%] text-center')}>
                  Triggered
                </div>
                <div className={cn('new-header2', 'w-[12%] text-center')}>
                  Active Chats
                </div>
                <div className={cn('new-header2', 'w-[12%] text-center')}>
                  Completed Chats
                </div>
                <div className={cn('new-header2', 'w-[11%] text-center')}>
                  Abandoned Chats
                </div>
              </div>
            )}

            <div
              className="min-w-full h-full min-h-[60vh] overflow-hidden overflow-y-scroll"
              id="scrollableDiv"
              ref={listContainerRef}
            >
              <InfiniteScroll
                dataLength={sequences?.length || 0}
                next={loadNextPage}
                hasMore={hasMore}
                scrollableTarget="scrollableDiv"
                height={loading ? '58vh' : '63vh'}
                className="!pr-1 max-[1600px]:pb-24 min-[1600px]:pb-24 listScroll"
                loader={
                  <div className="w-full h-[5vh] flex items-center justify-center bg-white rounded-lg mt-2">
                    <Loader Width={30} Height={30} />
                  </div>
                }
              >
                {sequences?.length > 0 ? (
                  <>
                    {sequences?.map((l) => {
                      const date = dateConverter(l?.created_at);
                      const statusConfig = SEQUENCE_STYLES[l?.status] || SEQUENCE_STYLES[0];

                      return (
                        <div
                          key={l?.id}
                          className="w-full odd:bg-gray-50 my-1 py-4 flex items-center rounded-lg pointer hover:bg-gray-100 transition-colors group/container"
                        >
                          {/* Name & Date & Actions */}
                          <div
                            className={cn(
                              bodyStyle,
                              'w-[35%] cursor-pointer pl-3 flex flex-col gap-1 pr-4 group',
                            )}
                            onClick={() => handleNavigateSequence(l)}
                          >
                            <div className="flex items-center gap-2">
                              {l?.name?.length > 25 ? (
                                <Tooltip title={l?.name} placement="top">
                                  <div className="new-header truncate max-w-[25ch] font-semibold text-[#202421]">
                                    {l?.name?.trim()?.charAt(0).toUpperCase() +
                                      l?.name?.trim()?.slice(1).toLowerCase()}
                                  </div>
                                </Tooltip>
                              ) : (
                                <div className="new-header font-semibold text-[#202421]">
                                  {l?.name && (
                                    <div>
                                      {l.name?.trim()?.charAt(0).toUpperCase() +
                                        l.name?.trim()?.slice(1).toLowerCase()}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            {/* Date & Action Icons Row */}
                            <div className="flex items-center gap-2">
                              <div className="text-xs text-gray-500">
                                Created @ {date?.monthInShort} {date?.date}{' '}
                                {date?.hours}:{date?.minutes}
                                {date?.meridiem}
                              </div>
                              {/* Action Buttons - Inline with date */}
                              <div className="flex items-center gap-1 opacity-0 group-hover/container:opacity-100 transition-opacity duration-200">
                                {/* Analytics — only for COMPLETED */}
                                {l?.status === 'COMPLETED' && (
                                  <Tooltip title="Analytics" placement="top">
                                    <button
                                      className="w-6 h-6 flex items-center justify-center rounded hover:bg-gray-200 transition-colors"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleNavigateSequence(l);
                                      }}
                                    >
                                      <img src={ICONS?.analytics} alt="Analytics" className="w-4 h-4" />
                                    </button>
                                  </Tooltip>
                                )}

                                {/* Edit Action — not for COMPLETED */}
                                {!['COMPLETED'].includes(l?.status) && (
                                  <Tooltip title="Edit" placement="top">
                                    <button
                                      className="w-6 h-6 flex items-center justify-center rounded hover:bg-gray-200 transition-colors"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEdit(l);
                                      }}
                                    >
                                      <Edit
                                        width="16px"
                                        height="16px"
                                        strokeWidth="1.7"
                                        className="stroke-[#898E99] hover:stroke-[var(--contentText)]"
                                      />
                                    </button>
                                  </Tooltip>
                                )}


                                {/* Pause Action — for ACTIVE, RUNNING, SCHEDULED */}
                                {['ACTIVE', 'RUNNING', 'SCHEDULED'].includes(l?.status) && (
                                  <Tooltip title="Pause" placement="top">
                                    <button
                                      className="w-6 h-6 flex items-center justify-center rounded hover:bg-gray-200 transition-colors"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handlePauseResume(l);
                                      }}
                                    >
                                      <img src={ICONS?.PausedBlack} alt="Pause" className="w-4 h-4" />
                                    </button>
                                  </Tooltip>
                                )}

                                {/* Resume Action — for PAUSED */}
                                {l?.status === 'PAUSED' && (
                                  <Tooltip title="Resume" placement="top">
                                    <button
                                      className="w-6 h-6 flex items-center justify-center rounded hover:bg-gray-200 transition-colors"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handlePauseResume(l);
                                      }}
                                    >
                                      <Play width={16} height={16} color="#898E99" />
                                    </button>
                                  </Tooltip>
                                )}

                              {/* Delete Action — only for NEW, DRAFT, SCHEDULED */}
                              {['NEW', 'DRAFT', 'SCHEDULED'].includes(l?.status) && (
                                <Tooltip title="Delete" placement="top">
                                  <button
                                      className="w-6 h-6 flex items-center justify-center rounded hover:bg-red-100 transition-colors group/delete"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDelete(l);
                                    }}
>
                                      <Delete
                                        width="16px"
                                        height="16px"
                                        className="[&>path]:stroke-[#898e99] [&>path]:transition-colors group-hover/delete:[&>path]:!stroke-[#ef4444]"
                                      />
                                  </button>
                                </Tooltip>
                              )}
                            </div>
                          </div>
                          </div>

                          {/* Status */}
                          <div
                            className={cn(
                              bodyStyle,
                              'w-[15%] flex items-center justify-center gap-1.5',
                            )}
                            onClick={() => handleNavigateSequence(l)}
                          >
                            <div
                              className="flex items-center gap-2.5 px-2.5 py-1 rounded-full"
                              style={{
                                background: statusConfig?.bg,
                                color: statusConfig?.color,
                                border: `1px solid ${statusConfig?.color}`,
                              }}
                            >
                              {statusConfig?.icon && (
                                <img src={statusConfig?.icon} alt={statusConfig?.text} />
                              )}
                              <p className="capitalize">{statusConfig?.text}</p>
                            </div>
                          </div>

                          {/* Triggered count */}
                          <div
                            className={cn('new-body-text', 'w-[15%] text-center')}
                            onClick={() => handleNavigateSequence(l)}
                          >
                            {l?.triggered_count?.toLocaleString() || '0'}
                          </div>

                          {/* Active count */}
                          <div
                            className={cn('new-body-text', 'w-[12%] text-center text-[#0F9960]')}
                            onClick={() => handleNavigateSequence(l)}
                          >
                            {l?.active_count?.toLocaleString() || '0'}
                          </div>

                          {/* Completed count */}
                          <div
                            className={cn('new-body-text', 'w-[12%] text-center text-[#0F9960]')}
                            onClick={() => handleNavigateSequence(l)}
                          >
                            {l?.completed_count?.toLocaleString() || '0'}
                          </div>

                          {/* Abandoned count */}
                          <div
                            className={cn('new-body-text', 'w-[11%] text-center text-[#6940F2]')}
                            onClick={() => handleNavigateSequence(l)}
                          >
                            {l?.abandoned_count?.toLocaleString() || '0'}
                          </div>
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <>
                    {!searchKey ? (
                      <div className="w-full h-full flex justify-center mt-20">
                        <NoData
                          title="No sequences yet"
                          content="Create your first sequence to automate customer engagement"
                          modal={
                            <div className="flex gap-3">
                              <button
                                className="flex-row align-center px-4 py-1.5 text-base weight-medium rounded-md bg-[var(--new-primary)] border-2 border-[var(--new-primary)] text-white transition-colors"
                                onClick={openCreateModal}
                              >
                                <PlusOutlined className="mr-2" />
                                Create Sequence
                              </button>
                            </div>
                          }
                          icon={ICONS?.campaignNoData}
                        />
                      </div>
                    ) : (
                      <div className="h-[60vh] flex justify-center mt-20 pt-20">
                        <div className="flex-row h-10 mt-20">
                          <img
                            src={ICONS?.emptySearch}
                            alt="emptySearch"
                            className="w-14"
                          />
                          <div className="flex-column gap-3 pl-10">
                            <span className="new-header3">
                              Sorry, there are no matching results
                            </span>
                            <span className="no-search-body">
                              Use different keywords or{' '}
                              <span
                                className="text-[#6940F2] pointer"
                                onClick={() => {
                                  setSearchKey('');
                                  clearStatus();
                                }}
                              >
                                reset filter.
                              </span>
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </InfiniteScroll>
            </div>
          </Skeleton>
        </div>
      </div>

      {/* Create Sequence Modal */}
      <Modal
        open={isCreateModalOpen}
        onCancel={closeCreateModal}
        footer={null}
        closable={false}
        centered
        width={630}
        styles={{
          content: { padding: 0, borderRadius: '16px', overflow: 'hidden' },
        }}
      >
        <Header
          title="Create Sequence"
          handleCancel={closeCreateModal}
          rootStyle={{
            backgroundColor: '#E6FFEB',
            borderBottom: '1px solid #D1F7D6',
          }}
          titleStyle={{ color: '#06441C', fontSize: '20px' }}
        />
        <div className="px-6 py-6 flex flex-col gap-5">
          {/* Name Input */}
          <div className="w-full flex flex-col gap-3.5">
            <label className="text-base weight-semibold">
              Name Your Sequence <span className="text-[#FF543E]">*</span>
            </label>
            <div className="w-full h-12 border border-gray-200 bg-transparent px-2.5 py-1 rounded-md flex items-center gap-2.5">
              <input
                type="text"
                value={sequenceData.name}
                placeholder="Enter sequence name"
                className="w-full h-full flex-1 outline-none text-sm weight-medium text-[#2D3036]"
                maxLength={MAX_SEQUENCE_NAME_LENGTH}
                onChange={(e) => {
                  const newValue = e.target.value.replace(/[^A-Za-z0-9-_ ]/g, '');
                  setSequenceData({ ...sequenceData, name: newValue });
                }}
              />
              <p className="text-[#616874]">
                {sequenceData.name?.length || 0}/{MAX_SEQUENCE_NAME_LENGTH}
              </p>
            </div>
          </div>

          {/* Duration Picker */}
          <div className="w-full flex flex-col gap-3.5">
            <label className="text-base weight-semibold">
              Duration <span className="text-[#FF543E]">*</span>
            </label>
            <div className="flex-1">
              <MonthCalender
                open={calendarOpen}
                setOpen={setCalendarOpen}
                allowedDate="allowed"
                dateRange={{
                  startDate: new Date().toISOString(),
                  endDate: null,
                }}
                primary={true}
                customRanges={false}
                placeholder="Please select the duration"
                startDate={sequenceData.start_date}
                endDate={sequenceData.end_date}
                onChange={(value) => {
                  setSequenceData({
                    ...sequenceData,
                    start_date: value?.start,
                    end_date: value?.end,
                  });
                }}
                rootClassName="w-full h-12 text-sm weight-medium text-[#2D3036] flex-1 justify-start bg-transparent border border-gray-200"
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="w-full h-16 flex items-center justify-end mt-3">
            <button
              className={cn(
                'w-fit h-fit px-4 py-2 rounded-md text-base weight-medium',
                canCreateSequence() && !isCreating
                  ? 'bg-[#05805C] text-white cursor-pointer'
                  : 'bg-[var(--BG-100)] text-[var(--font-600)] cursor-not-allowed'
              )}
              disabled={!canCreateSequence() || isCreating}
              onClick={handleCreateSequence}
            >
              {isCreating ? 'Creating...' : 'Create Sequence'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
      ) : (
        <PremiumUpgradeScreen />
      )}
    </>
  );
};

export default SequencesList;
